import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import './MyQuotations.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MyQuotations = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const socket = useSocket();
  
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchQuotations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const enrichedQuotes = await Promise.all((data.bids || []).map(async (bid) => {
          try {
            const rfqRes = await fetch(`${API_BASE}/rfqs/${bid.rfqId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rfqData = await rfqRes.json();
            return { 
              ...bid, 
              vesselName: rfqData.rfq?.vesselName || 'N/A', 
              serviceType: rfqData.rfq?.serviceType || 'N/A', 
              port: rfqData.rfq?.port || 'N/A', 
              deadline: rfqData.rfq?.deadline || null 
            };
          } catch (err) { 
            return { ...bid, vesselName: 'N/A', serviceType: 'N/A', port: 'N/A' }; 
          }
        }));
        setQuotations(enrichedQuotes);
      } else {
        setError(data.message || 'Failed to fetch quotations');
      }
    } catch (err) { 
      setError('Network error. Please try again.'); 
    } finally { 
      setLoading(false); 
    }
  }, [token]);

  useEffect(() => { 
    fetchQuotations(); 
  }, [fetchQuotations]);

  useEffect(() => { 
    if (socket) { 
      socket.on('bid_accepted', fetchQuotations); 
      socket.on('new_bid', fetchQuotations); 
      return () => { 
        socket.off('bid_accepted', fetchQuotations); 
        socket.off('new_bid', fetchQuotations); 
      }; 
    } 
  }, [socket, fetchQuotations]);

  const filteredQuotations = useMemo(() => {
    return quotations.filter(q => 
      q.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      q.serviceType?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      q._id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, quotations]);

  const handleWithdrawQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to withdraw this quotation?')) return;
    try {
      const res = await fetch(`${API_BASE}/bids/${quoteId}`, { 
        method: 'DELETE', 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = await res.json();
      if (data.success) {
        fetchQuotations();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert('Error withdrawing quotation');
    }
  };

  const handleEditQuote = (quoteId) => {
    navigate(`/service-provider/edit-quotation/${quoteId}`);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': 
        return <span className="q-status is-pending">Pending</span>;
      case 'accepted': 
        return <span className="q-status is-accepted">Accepted</span>;
      case 'rejected': 
        return <span className="q-status is-rejected">Rejected</span>;
      case 'revised': 
        return <span className="q-status is-revised">Revised</span>;
      default: 
        return <span className="q-status">{status}</span>;
    }
  };

  if (loading) return <div className="q-loader-wrap"><i className="fas fa-circle-notch fa-spin"></i></div>;

  return (
    <div className="q-clean-container">
      <div className="q-clean-header">
        <div className="q-header-info">
          <h1>My Quotations</h1>
          <p>Track your submitted service proposals and status updates</p>
        </div>
        <button className="q-action-primary-btn" onClick={() => navigate('/service-provider/available-jobs')}>
          <i className="fas fa-search"></i> Browse Jobs
        </button>
      </div>

      <div className="q-toolbar-strip">
        <div className="q-search-input-wrapper">
          <i className="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Filter by vessel name, ID, or service scope..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>
        <div className="q-records-badge">
          {filteredQuotations.length} items
        </div>
      </div>

      {error ? (
        <div className="q-panel-state error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      ) : filteredQuotations.length === 0 ? (
        <div className="q-panel-state empty">
          <i className="fas fa-file-invoice"></i>
          <h3>No quotations found</h3>
          <p>You haven't submitted any quotations yet.</p>
        </div>
      ) : (
        <div className="q-table-card">
          <table className="q-table-minimal">
            <thead>
              <tr>
                <th>Quote ID</th>
                <th>Vessel</th>
                <th>Port</th>
                <th>Service Type</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotations.map(quote => (
                <tr key={quote._id}>
                  <td className="q-id-text">#{quote._id?.slice(-8).toUpperCase()}</td>
                  <td className="q-vessel-name">{quote.vesselName}</td>
                  <td className="q-port-cell">
                    <i className="fas fa-map-marker-alt"></i> {quote.port}
                  </td>
                  <td className="q-service-text">{quote.serviceType}</td>
                  <td>{getStatusBadge(quote.status)}</td>
                  <td className="text-right">
                    <div className="q-actions-row">
                      <button className="q-row-btn" title="View Document" onClick={() => navigate(`/service-provider/quote/${quote._id}`)}>
                        <i className="far fa-eye"></i>
                      </button>
                      {quote.status === 'pending' && (
                        <>
                          <button className="q-row-btn" title="Edit Proposal" onClick={() => handleEditQuote(quote._id)}>
                            <i className="far fa-edit"></i>
                          </button>
                          <button className="q-row-btn is-danger" title="Withdraw" onClick={() => handleWithdrawQuote(quote._id)}>
                            <i className="far fa-trash-alt"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MyQuotations;